import { Alert, Button, Card, DatePicker, Form, Input, Select, Space, message } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Dayjs } from 'dayjs';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationSessionSelect,
  EducationStaffSelect,
} from '@/pages/education/components/EducationLookupSelects';

type LeaveOption = {
  id: string;
  leave_application_number?: string;
  leave_type?: string;
  status?: string;
  from_date?: string;
  to_date?: string;
};

type FormValues = {
  session_id: string;
  absence_date: Dayjs;
  absent_staff_link_id: string;
  hr_leave_application_id?: string;
  reason?: string;
};

export default function SubstitutionDayFormPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.substitution.manage') ||
      user?.permissions?.includes('education.admin')
  );
  const [form] = Form.useForm<FormValues>();
  const [staffLinkId, setStaffLinkId] = useState<string | undefined>();
  const [absenceDate, setAbsenceDate] = useState<string | undefined>();

  const leaveOptions = useQuery({
    queryKey: ['education-substitution-leave-options', staffLinkId, absenceDate],
    enabled: Boolean(staffLinkId) && Boolean(absenceDate),
    queryFn: async () => {
      const res = await api.get<LeaveOption[]>('/education/substitutions/leave-options', {
        params: { staff_link_id: staffLinkId, absence_date: absenceDate },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const create = useMutation({
    mutationFn: (values: FormValues) =>
      api.post<{ day: { id: string } }>('/education/substitutions', {
        session_id: values.session_id,
        absence_date: values.absence_date.format('YYYY-MM-DD'),
        absent_staff_link_id: values.absent_staff_link_id,
        hr_leave_application_id: values.hr_leave_application_id || null,
        reason: values.reason || null,
      }),
    onSuccess: (res) => {
      message.success(t('substitutionDayCreated'));
      const body = res.data as { day?: { id?: string }; id?: string };
      const id = body?.day?.id || body?.id;
      if (id) navigate(`/education/substitutions/${id}`);
      else navigate('/education/substitutions');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={t('newSubstitutionDay')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('newSubstitutionDay')}
      description={t('newSubstitutionDayIntro')}
      extra={<Button onClick={() => navigate('/education/substitutions')}>{t('backToSubstitutions')}</Button>}
    >
      <Card size="small" style={{ maxWidth: 640 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => create.mutate(values)}
        >
          <Form.Item name="session_id" label={t('session')} rules={[{ required: true }]}>
            <EducationSessionSelect style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="absence_date" label={t('absenceDate')} rules={[{ required: true }]}>
            <DatePicker
              style={{ width: '100%' }}
              onChange={(v) => {
                setAbsenceDate(v ? v.format('YYYY-MM-DD') : undefined);
                form.setFieldValue('hr_leave_application_id', undefined);
              }}
            />
          </Form.Item>
          <Form.Item name="absent_staff_link_id" label={t('absentTeacher')} rules={[{ required: true }]}>
            <EducationStaffSelect
              style={{ width: '100%' }}
              onChange={(v) => {
                setStaffLinkId(v || undefined);
                form.setFieldValue('hr_leave_application_id', undefined);
              }}
            />
          </Form.Item>
          <Form.Item
            name="hr_leave_application_id"
            label={t('hrLeaveOptional')}
            extra={t('hrLeaveOptionalHint')}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              disabled={!staffLinkId || !absenceDate}
              loading={leaveOptions.isFetching}
              options={(leaveOptions.data ?? []).map((row) => ({
                value: row.id,
                label: `${row.leave_application_number || row.id.slice(0, 8)} — ${row.leave_type || ''} (${row.status}) ${String(row.from_date || '').slice(0, 10)}→${String(row.to_date || '').slice(0, 10)}`,
              }))}
              notFoundContent={
                leaveOptions.isFetching
                  ? t('loading', { defaultValue: 'Loading…' })
                  : t('hrLeaveNone')
              }
            />
          </Form.Item>
          <Form.Item name="reason" label={t('reason')}>
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('substitutionMaterializeHint')}
          />
          <Space>
            <Button type="primary" htmlType="submit" loading={create.isPending}>
              {t('create')}
            </Button>
            <Button onClick={() => navigate('/education/substitutions')}>{t('cancel')}</Button>
          </Space>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
